<template>
  <nav
    id="navigation"
    class="main-navigation"
  >
    <ul
      :class="setStickyStyling ? 'lg:opacity-0 pointer-events-none' : 'opacity-100'"
      class="main-navigation-list transition-opacity"
    >
      <li
        v-if="navMainSearchAction"
        class="main-navigation-list-item main-navigation-list-item--search mb-4 mb:mb-0"
      >
        <kf-search-bar
          class="search-bar-wrapper-small"
          :search-action="navMainSearchAction"
          :search-placeholder="navMainSearchPlaceholder"
        />
      </li>
      <li
        v-for="(item, index) in navMainItems"
        :key="item.Name"
        class="main-navigation-list-item"
      >
        <a
          v-if="item.UrlSegment"
          :href="`${item.UrlSegment}`"
          :target="item.target ? '_blank' : null"
          class="main-navigation-list-link"
          :class="{ ['is-active']: isUrlCurrentlyActive(item.UrlSegment) }"
          @click="scrollToElementIfLinkIsLocal(item.UrlSegment, $event)"
        >
          {{ item.Name }}
        </a>
        <button
          v-else
          class="main-navigation-list-link is-toggle"
          :class="{ 'is-open': index === selectedSub && navSubState }"
          @click.prevent="openSub(item, index)"
        >
          {{ item.Name }}
        </button>
        <ul
          v-if="item.navigationSub?.length"
          class="sub-navigation-list-small"
          :class="{ 'is-visible': index === selectedSub }"
        >
          <template v-for="subItem in item.navigationSub">
            <li
              v-if="subItem.href && subItem.label"
              :key="subItem.label"
              class="sub-navigation-list-item-small"
            >
              <a
                :href="subItem.href"
                class="sub-navigation-list-link-small"
              >
                {{ subItem.label }}
              </a>
            </li>
          </template>
        </ul>
      </li>
    </ul>
    <div class="main-navigation-end">
      <kf-search-bar
        v-if="navMainSearchAction"
        :search-action="navMainSearchAction"
        :search-placeholder="navMainSearchPlaceholder"
      />
      <kf-link
        v-if="navMainCta"
        type="button-primary"
        :href="navMainCta.href"
        @handleClick="fireEvent(navMainCta.trackData)"
      >
        {{ navMainCta.label }}
      </kf-link>
    </div>
    <slot />
  </nav>
</template>

<script>
import KfLink from '@/components/base/link/Link.vue';
import KfSearchBar from '@/components/search/SearchBar.vue';
import { trackEvent } from '@/lib/gtm/trackEvent.js';

export default {
  name: 'KfNavigationMain',
  components: {
    KfSearchBar,
    KfLink,
  },
  props: {
    setStickyStyling: {
      type: Boolean,
      default: true,
    },
    navMainItems: {
      type: Array,
      default: null,
    },
    navMainSearchAction: {
      type: String,
      default: null,
    },
    navMainSearchPlaceholder: {
      type: String,
      default: null,
    },
    navMainCta: {
      type: Object,
      default: null,
    },
    navSubState: {
      type: Boolean,
      default: null,
    },
  },

  data() {
    return {
      selectedSub: null,
      localNavSubState: false,
    };
  },

  methods: {
    openSub(item, index) {
      if (this.selectedSub === index) {
        this.selectedSub = null;
      } else {
        this.selectedSub = index;
      }
      this.$emit('clicked', item);
    },
    isUrlCurrentlyActive(url) {
      return window.location.href.startsWith(url) || window.location.pathname.startsWith(url);
    },
    scrollToElementIfLinkIsLocal(url, event) {
      if (url.startsWith('#')) {
        const targetElement = document.querySelector(url);

        event.preventDefault();

        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth' });
        }
      }
    },
    fireEvent(data) {
      trackEvent(data, this.$gtm.enabled());
    },
  },
};
</script>

<style lang="postcss">
.main-navigation {
  @apply w-full items-end flex flex-col justify-between absolute z-10 overflow-hidden left-0 top-0 opacity-0;
  transform: translateY(-100%);
  transition: all 350ms;

  @screen lg {
    @apply relative h-full top-auto flex flex-row opacity-100;
    height: 55px;
    transform: translateY(0);
    overflow: inherit;
  }

  &.is-open {
    @apply bg-white opacity-100 shadow-md;
    transform: translateY(0);
    top: 60px;

    @screen lg {
      @apply top-0 bg-transparent;
    }
  }

  &-list {
    @apply relative h-full items-start flex flex-col px-8 list-none w-full pt-8;

    @screen md {
      @apply pt-14;
    }

    @screen lg {
      @apply flex-row pt-0 px-0;
    }

    &-item {
      @apply relative flex flex-col items-center justify-center w-full;

      @screen lg {
        @apply justify-end h-full mr-10 w-auto;
      }

      &--search {
        @apply justify-end md:hidden;
      }

      &:last-of-type {
        @apply border-b border-gray-200;

        @screen lg {
          @apply border-0;
        }
      }
    }

    &-link {
      @apply w-full text-primary-bravo font-bold mb-4 block no-underline text-xl;
      .theme-secondary & {
        &::after {
          background: transparent;
        }
      }

      @screen lg {
        @apply font-normal mb-0 inline text-black pb-4;

        &::after {
          content: '';
          @apply absolute block w-0 h-1;
          border-radius: 1px;
          background: theme('colors.primary.bravo');
          transition: width 0.3s;
          bottom: -1px;
        }

        &:hover {
          @apply no-underline text-black;

          &::after {
            width: 100%;
          }
        }

        &.is-active {
          &::after {
            width: 100%;
            transition: none;
          }
        }
      }

      @screen md {
        @apply text-lg;
      }

      &.is-toggle {
        @apply relative pr-6 text-left;

        &::before {
          @apply absolute bg-no-repeat right-0;
          content: '';
          height: 13px;
          width: 13px;
          top: 7px;
          background-size: 13px 13px;
          background-image: url('@/assets/svg/chevron-down-secondary.svg');
          background-position: right;
          transition: transform 250ms ease;
        }
      }

      .theme-secondary & {
        @apply relative w-full text-primary-bravo font-bold mb-4 block no-underline text-xl;
        position: relative;

        @screen lg {
          @apply mb-0 inline text-black pb-4;
        }

        &:hover {
          color: var(--color-primary-alpha);
          text-decoration: none !important;
        }
        &::before {
          background-image: url('@/assets/svg/theme-secondary/ts-chevron-down-secondary.svg');
        }
      }

      &.is-open {
        &::before {
          transform: rotate(180deg);
        }

        @screen lg {
          &::after {
            content: '';
            @apply absolute block w-full h-1;
            border-radius: 1px;
            background: theme('colors.primary.bravo');
            transition: width 0.3s;
            bottom: -1px;
          }
        }

        + .sub-navigation-list-small {
          @apply block;

          @screen lg {
            @apply hidden;
          }
        }
      }
    }

    .sub-navigation-list-small {
      @apply hidden px-7 w-full text-left;

      @screen lg {
        @apply hidden;
      }

      .sub-navigation-list-item-small {
        @apply mb-3;
      }

      .sub-navigation-list-link-small {
        @apply no-underline text-primary-bravo;
      }
    }
  }

  &-end {
    @apply flex;

    .link.is-button-primary {
      @apply py-2 mb-3 ml-4 hidden;

      @screen lg {
        @apply flex text-base;
      }
    }
  }

  .search-bar-wrapper-small {
    @apply flex mb-2;

    @screen lg {
      @apply hidden;
    }
  }
}
</style>
